import React, {MouseEvent, useCallback, useEffect, useState} from 'react';
import './css';
import {isMobile} from "react-device-detect";
import classNames from "classnames";
import 'swiper/css/swiper.css'
import {IMainStore} from "./stores/MainStore";
import {ReactProps} from "./constants/Props";
import Pager from "./components/Pager";
import Screens from "./constants/Screens";
import {observer} from "mobx-react";
import PhoneNumberScreen from "./screens/PhoneNumberScreen";
import ConfirmationCodeScreen from "./screens/ConfirmationCodeScreen";
import CreateGroupScreen from "./screens/CreateGroupScreen";
import HLPrivacySettingScreen from "./screens/HLPrivacySettingScreen";
import SelectGroupScreen from "./screens/SelectGroupScreen";
import OnboardingScreen from "./screens/OnboardingScreen";
import LandingPageScreen from "./screens/LandingPageScreen";
import GenerateLinkScreen from "./screens/GenerateLinkScreen";
import LinkDetailsScreen from "./screens/LinkDetailsScreen";
import WelcomeScreen from "./screens/WelcomeScreen";
import styles from "./css/App.module.css"
import EmailScreen from "./screens/EmailScreen";
import IconGroupAvatar from "./images/ico_group_avatar.png"
import GetStartedScreen from "./screens/GetStartedScreen";
import PreviewScreen from "./screens/PreviewScreen";
import {generatePreviewObject} from "./utils/PreviewHelper";
import 'mobx-react-lite/batchingForReactDom'
import {GlobalContext} from './constants/Providers';
import {toI18N} from "./utils/I18n";
import ModalConfirmation from "./components/ModalConfirmation";
import GroupSettingScreen from "./screens/GroupSettingScreen";
import AddWelcomePostScreen from "./screens/AddWelcomePostScreen";
import { getSignUpURL } from './utils/LoginUtils';
// import AddBioGroupScreen from "./screens/AddBioGroupScreen";

interface Props {
    store: IMainStore
}

function App(props: Props) {

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const previewBase64 = urlParams.get('preview');

    // Props

    const store = props.store;

    const {
        isStarted,
        currentPage,
        previousPage,
        changeScreen,
        forkLink,
        deeplink: {
            isLoading: hasNotGeneratedDeeplink
        },
        auth: {
            loggedIn,
            isInMiddleOfLoggingIn
        }
    } = store;

    // State

    const [confirmModal, setConfirmModal] = useState<{ show: boolean, message?: string, onConfirm?: (e: MouseEvent) => void }>({
        show: false,
        message: undefined,
        onConfirm: undefined
    });

    // Callbacks

    const navigateTo = useCallback((screen: Screens) => {
        changeScreen(screen)
    }, [changeScreen]);

    const shouldRenderScreen = useCallback((screen: Screens) => {
        return currentPage === screen || previousPage === screen
    }, [currentPage, previousPage]);

    const onLogin = useCallback(() => {
        if (loggedIn) {
            navigateTo(Screens.getStarted)
        } else {
            const url = getSignUpURL()
            window.open(url, "_self")
        }
    }, [loggedIn, navigateTo]);

    const onLogout = useCallback(() => {
        setConfirmModal({
            show: true,
            message: toI18N("getting_started_logout_confirm"),
            onConfirm: () => {
                store.logout();
                setConfirmModal({show: false});
                navigateTo(Screens.welcome);
            }
        });

    }, [store, setConfirmModal, navigateTo]);

    const onFork = useCallback(() => {

        setConfirmModal({
            show: true,
            message: toI18N("are_you_sure_you_want_to_fork"),
            onConfirm: () => {
                forkLink();
                setConfirmModal({show: false});
            }
        });

    }, [forkLink, setConfirmModal]);

    // Effects

    // TODO: Remove this just for dev purpose

    // useEffect(() => {
    //
    //     setTimeout(() => {
    //         store.changeScreen(Screens.generateLink);
    //     }, 100);
    //
    //     // eslint-disable-next-line
    // }, []);

    useEffect(() => {
        document.documentElement.style.setProperty('--mobile', isMobile ? '1' : '0');
    }, []);

    useEffect(() => {
        async function startup() {
            store.fetchAndSetDefaultCountryCode();
            await store.auth.loginWithRefreshToken();
        }

        startup().then();
        // eslint-disable-next-line
    }, []); // Mimic componentDidMount

    useEffect(() => {
        window.onbeforeunload = () => {
            if (isStarted && hasNotGeneratedDeeplink) {
                return '';
            }
            return null;
        };
    }, [isStarted, hasNotGeneratedDeeplink]);

    const SurroundingElement = isMobile ? 'div' : MobileMockup;
    const style = !isMobile && window.innerHeight < 812 ? {transform: `scale(${window.innerHeight / 864})`} : undefined;

    return (
        <GlobalContext.Provider value={{
            editingDeeplinkUrl: store.deeplink.linkId ? store.deeplink.url : null,
            editDeeplinkIsExpired: store.deeplink.isExpired,
            fork: onFork
        }}>
            <main style={style} className={classNames(styles.mainContainer, {
                [styles.mainContainerMobile]: isMobile,
            })}>
                <SurroundingElement className={classNames({
                    [styles.mainContainerMobileChild]: isMobile,
                })}>

                    {
                        previewBase64 ?

                            (() => {

                                const preview = generatePreviewObject(previewBase64);

                                return <PreviewScreen
                                    firstScreen={preview.firstScreenStore}
                                    secondScreen={preview.secondScreenStore}
                                    landingPage={preview.landingPageStore}
                                    groupAvatar={preview.group.avatar}
                                    previewHint={preview.previewHint}
                                    user={preview.user}
                                    group={preview.group}
                                />;

                            })() :

                            <Pager page={currentPage}>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.welcome) &&
                                        <WelcomeScreen
                                            isInMiddleOfLoggingIn={isInMiddleOfLoggingIn}
                                            loggedIn={loggedIn}
                                            isActive={currentPage === Screens.welcome}
                                            onNext={onLogin}
                                        />
                                    }
                                </div>

                                <div>
                                    {
                                        shouldRenderScreen(Screens.phoneNumber) &&
                                        <PhoneNumberScreen
                                          isActive={currentPage === Screens.phoneNumber}
                                          onBack={navigateTo.bind(null, Screens.welcome)}
                                          onNext={navigateTo.bind(null, Screens.confirmationCode)}
                                          store={store.auth}
                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.confirmationCode) &&
                                        <ConfirmationCodeScreen
                                          isActive={currentPage === Screens.confirmationCode}
                                          onBack={navigateTo.bind(null, Screens.phoneNumber)}
                                          openSelectGroup={navigateTo.bind(null, Screens.getStarted)}
                                          openCreateGroup={navigateTo.bind(null, Screens.getStarted)}
                                          openGetStartedScreen={navigateTo.bind(null, Screens.getStarted)}
                                          store={store.auth}
                                          deeplinkStore={store.deeplink}

                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.getStarted) &&
                                        <GetStartedScreen
                                          isActive={currentPage === Screens.getStarted}
                                          isInMiddleOfLoggingIn={isInMiddleOfLoggingIn}
                                          onBack={navigateTo.bind(null, Screens.welcome)}
                                          openPhoneNumber={navigateTo.bind(null, Screens.phoneNumber)}
                                          openCreateGroup={navigateTo.bind(null, Screens.createGroup)}
                                          openSelectGroup={navigateTo.bind(null, Screens.selectGroup)}
                                          login={navigateTo.bind(null, Screens.phoneNumber)}
                                          fetchLink={store.fetchLink}
                                          resetLinkStore={store.resetLinkStore}
                                          authStore={store.auth}
                                          groupStore={store.group}
                                          logout={onLogout}
                                          store={store.deeplink}
                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.selectGroup) &&
                                        <SelectGroupScreen
                                          groups={store.groups}
                                          isActive={currentPage === Screens.selectGroup}
                                          onBack={navigateTo.bind(null, Screens.getStarted)}
                                          onNext={navigateTo.bind(null, store.deeplink.linkId ? Screens.onboarding1 : Screens.email)}
                                          markDeeplinkAsExpired={store.markDeeplinkAsExpired}
                                          setConfirmModal={setConfirmModal}
                                          store={store.group}
                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.createGroup) &&
                                        <CreateGroupScreen
                                          groups={store.groups}
                                          isActive={currentPage === Screens.createGroup}
                                          onBack={navigateTo.bind(null, Screens.getStarted)}
                                          onNext={navigateTo.bind(null, Screens.hlPrivacySetting)}
                                          store={store.group}
                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.hlPrivacySetting) &&
                                        <HLPrivacySettingScreen
                                          linkStore={store.deeplink}
                                          isActive={currentPage === Screens.hlPrivacySetting}
                                          store={store.group}
                                          onBack={navigateTo.bind(null, Screens.createGroup)}
                                          onNext={navigateTo.bind(null, Screens.addWelcomePost)}
                                        />
                                    }
                                </div>
                                {/*<div>*/}
                                {/*    {*/}
                                {/*        shouldRenderScreen(Screens.addBioGroup) &&*/}
                                {/*        <AddBioGroupScreen*/}
                                {/*            store={store.group}*/}
                                {/*            onBack={navigateTo.bind(null, Screens.hlPrivacySetting)}*/}
                                {/*            isActive={currentPage === Screens.addBioGroup}*/}
                                {/*            onNext={navigateTo.bind(null, Screens.addWelcomePost)}*/}
                                {/*        />*/}
                                {/*    }*/}
                                {/*</div>*/}
                                <div>
                                    {
                                        shouldRenderScreen(Screens.addWelcomePost) &&
                                        <AddWelcomePostScreen
                                          store={store.group}
                                          onBack={navigateTo.bind(null, Screens.hlPrivacySetting)}
                                          isActive={currentPage === Screens.addWelcomePost}
                                          onNext={navigateTo.bind(null, Screens.groupSettingScreen)}
                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.groupSettingScreen) &&
                                        <GroupSettingScreen
                                          store={store.group}
                                          groups={store.groups}
                                          isActive={currentPage === Screens.groupSettingScreen}
                                          onBack={navigateTo.bind(null, Screens.addWelcomePost)}
                                          onNext={navigateTo.bind(null, store.deeplink.linkId ? Screens.onboarding1 : Screens.email)}
                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.email) &&
                                        <EmailScreen
                                          onBack={navigateTo.bind(null, !store.deeplink.isNewGroup ? Screens.selectGroup : Screens.groupSettingScreen)}
                                          isActive={currentPage === Screens.email}
                                          onNext={navigateTo.bind(null, Screens.onboarding1)}
                                          store={store.auth}
                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.onboarding1) &&
                                        <OnboardingScreen
                                            title={"first_onboarding_screen_header_title"}
                                            subtitle={"first_onboarding_screen_header_subtitle"}
                                            number={1}
                                            setLandingPageBackgroundColor={store.landingPage.setBackgroundColor}
                                            isActive={currentPage === Screens.onboarding1}
                                            setLandingPageTextColor={store.landingPage.setTextColor}
                                            groupAvatar={store.group.avatar}
                                            onBack={navigateTo.bind(null, store.deeplink.linkId ? Screens.selectGroup : Screens.email)}
                                            onNext={navigateTo.bind(null, Screens.onboarding2)}
                                            store={store.firstScreen}
                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.onboarding2) &&
                                        <OnboardingScreen
                                            setLandingPageBackgroundColor={store.landingPage.setBackgroundColor}
                                            setLandingPageTextColor={store.landingPage.setTextColor}
                                            isSkippable
                                            isActive={currentPage === Screens.onboarding2}
                                            title={"second_onboarding_screen_header_title"}
                                            subtitle={"second_onboarding_screen_header_subtitle"}
                                            number={2}
                                            groupAvatar={store.group.avatar}
                                            onBack={navigateTo.bind(null, Screens.onboarding1)}
                                            onNext={navigateTo.bind(null, Screens.landingPage)}
                                            store={store.secondScreen}
                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.landingPage) &&
                                        <LandingPageScreen
                                            store={store.landingPage}
                                            onBack={navigateTo.bind(null, Screens.onboarding2)}
                                            isActive={currentPage === Screens.landingPage}
                                            onNext={navigateTo.bind(null, Screens.linkDetails)}
                                            user={
                                                store.auth.loggedIn && store.deeplink.hasInviter ? {
                                                    name: store.auth.name,
                                                    avatar: store.auth.avatar
                                                } : undefined
                                            }
                                            group={
                                                {
                                                    name: store.group.name,
                                                    avatar: store.group.avatar ? store.group.avatar.path : IconGroupAvatar
                                                }
                                            }
                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.linkDetails) &&
                                        <LinkDetailsScreen
                                          landingPagePreviewLink={store.landingPagePreviewLink}
                                          csbPreviewLink={store.csbPreviewLink}
                                          textColor={store.landingPage.textColor}
                                          isActive={currentPage === Screens.linkDetails}
                                          onBack={navigateTo.bind(null, Screens.landingPage)}
                                          onNext={navigateTo.bind(null, Screens.generateLink)}
                                          checkIsHarmful={store.checkIsHarmful}
                                          onOpenPreview={navigateTo.bind(null, Screens.previewScreen)}
                                          authStore={store.auth}
                                          user={ { name: store.auth.name, avatar: store.auth.avatar } }
                                          setConfirmModal={setConfirmModal}
                                          imagesAreValid={store.imagesAreValid}
                                          reUploadAll={store.reUploadAll}
                                          store={store.deeplink}
                                          groupStore={store.group}
                                          authenticated={store.auth.loggedIn}
                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.generateLink) &&
                                        <GenerateLinkScreen
                                          fontColor={store.deeplinkData.fontColor}
                                          store={store.deeplink}
                                          defaultCountryCode={store.auth.defaultCountryCode}
                                          isActive={currentPage === Screens.generateLink}
                                          generateDeeplink={store.generateDeeplink}
                                          groupName={store.group.name}
                                          groupId={store.group.id}
                                        />
                                    }
                                </div>
                                <div>
                                    {
                                        shouldRenderScreen(Screens.previewScreen) &&
                                        <PreviewScreen
                                            firstScreen={store.firstScreen}
                                            secondScreen={store.secondScreen}
                                            landingPage={store.landingPage}
                                            groupAvatar={store.group.avatar}
                                            user={
                                                store.auth.loggedIn && store.deeplink.hasInviter ? {
                                                    name: store.auth.name,
                                                    avatar: store.auth.avatar
                                                } : undefined
                                            }
                                            group={
                                                {
                                                    name: store.group.name,
                                                    avatar: store.group.avatar ? store.group.avatar.path : IconGroupAvatar
                                                }
                                            }
                                            onClose={navigateTo.bind(null, Screens.linkDetails)}/>
                                    }
                                </div>
                            </Pager>

                    }

                    <ModalConfirmation
                        id={`main-app-modal-confirmation`}
                        message={confirmModal.message || ''}
                        onDismiss={setConfirmModal.bind(null, { show: false })}
                        onConfirm={confirmModal.onConfirm}
                        show={confirmModal.show}
                    />

                </SurroundingElement>
            </main>
        </GlobalContext.Provider>
    );
}

function MobileMockup(props: ReactProps) {

    return (
        <div {...props} className={styles.mobileMockup}>
            <div className={styles.mobileMockupChild}>
                {props.children}
            </div>
        </div>
    );

}

export default observer(App);
